import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  getAddressString,
  getMultilingualString,
  phoneNumberRegex,
  updateMultilingual,
} from "shared/utils";
import { SocialLinks } from "social-links";
import {
  EnumAddressLocationType,
  OrganizationByIdQuery,
  OrganizationUpdateMutationVariables,
  useOrganizationUpdateMutation,
} from "gql/generated";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";

const socialLinks = new SocialLinks();

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]);

export const organizationProfileSchema = z.object({
  name: z
    .string({
      required_error: `${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  website: z
    .string()
    .url(
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.WEBSITE")}. ${i18n.t("core:FORM_ERRORS.ADD_HTTP")}`,
    )
    .optional()
    .or(z.literal("")),
  phone: z
    .string()
    .min(
      1,
      `${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.PHONE")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    )
    .regex(
      phoneNumberRegex,
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.PHONE")}`,
    ),
  email: z
    .string()
    .min(
      1,
      `${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.EMAIL")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    )
    .email(
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.EMAIL")}`,
    ),
  fullAddress: z
    .string()
    .min(
      1,
      `${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.ADDRESS")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    ),
  fullAddressObj: z.object({
    line1: z
      .string()
      .min(
        1,
        `${i18n.t("core:SELECT_ADDRESS.BUSINESS_ADDRESS")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    line2: z.string().default(""),
    city: z
      .string()
      .min(
        1,
        `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.CITY")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    subdivision: z
      .string()
      .min(
        1,
        `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.SUBDIVISION")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    postalCode: z
      .string()
      .min(
        1,
        `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.POSTAL_CODE")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    country: z
      .string()
      .min(
        1,
        `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.COUNTRY")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    location: z.object({
      type: z.string().default("Point"),
      coordinates: z.object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
      }),
    }),
  }),
  description: z
    .string()
    .trim()
    .min(
      1,
      `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.DESCRIPTION")}`,
    )
    .optional()
    .or(z.literal("")),
  shortDescription: z
    .string()
    .trim()
    .min(
      1,
      `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.SHORT_DESCRIPTION")}`,
    )
    .optional()
    .or(z.literal("")),
  facebook: z
    .string()
    .refine((data) => socialLinks.isValid("facebook", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.FACEBOOK")}`,
    })
    .optional()
    .or(z.literal("")),
  instagram: z
    .string()
    .refine((data) => socialLinks.isValid("instagram", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.INSTAGRAM")}`,
    })
    .optional()
    .or(z.literal("")),
  x: z.string().optional().or(z.literal("")),
  youtube: z
    .string()
    .refine((data) => socialLinks.isValid("youtube", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.YOUTUBE")}`,
    })
    .optional()
    .or(z.literal("")),
  pinterest: z
    .string()
    .refine((data) => socialLinks.isValid("pinterest", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.PINTEREST")}`,
    })
    .optional()
    .or(z.literal("")),
  googleMyBusiness: z.string().optional().or(z.literal("")),
  tiktok: z
    .string()
    .refine((data) => socialLinks.isValid("tiktok", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:ORGANIZATION.PROFILE.FORM.LABELS.TIKTOK")}`,
    })
    .optional()
    .or(z.literal("")),
});

export type OrganizationProfileFormValues = z.infer<
  typeof organizationProfileSchema
>;

type UseOrganizationProfileFormProps = {
  organization: OrganizationByIdQuery["organizationById"];
};

export const useOrganizationProfileForm = ({
  organization,
}: UseOrganizationProfileFormProps) => {
  const defaultValues: OrganizationProfileFormValues = {
    description: organization?.description
      ? getMultilingualString(organization.description)
      : "",
    shortDescription: organization?.shortDescription
      ? getMultilingualString(organization.shortDescription)
      : "",
    name: organization?.name ? getMultilingualString(organization.name) : "",
    email: organization?.contact?.email ?? "",
    fullAddress: organization?.contact?.address
      ? getAddressString(organization.contact.address)
      : "",
    phone: organization?.contact?.phone ?? "",
    website: organization?.contact?.links?.website ?? "",
    fullAddressObj: {
      city: organization?.contact?.address?.city ?? "",
      country: organization?.contact?.address?.country ?? "",
      line1: organization?.contact?.address?.line1 ?? "",
      line2: organization?.contact?.address?.line2 ?? "",
      location: {
        type: organization?.contact?.address?.location?.type ?? "Point",
        coordinates: {
          lat: organization?.contact?.address?.location?.coordinates[1] ?? 0,
          lng: organization?.contact?.address?.location?.coordinates[0] ?? 0,
        },
      },
      postalCode: organization?.contact?.address?.postalCode ?? "",
      subdivision: organization?.contact?.address?.subdivision ?? "",
    },
    facebook: organization?.contact?.links?.facebook ?? "",
    googleMyBusiness: organization?.contact?.links?.googleMyBusiness ?? "",
    instagram: organization?.contact?.links?.instagram ?? "",
    pinterest: organization?.contact?.links?.pinterest ?? "",
    x: organization?.contact?.links?.x ?? "",
    tiktok: organization?.contact?.links?.tiktok ?? "",
    youtube: organization?.contact?.links?.youtube ?? "",
  };

  const [updateOrganization] = useOrganizationUpdateMutation();
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  return useFormHandler<OrganizationProfileFormValues>(
    useCallback(
      async (data) => {
        if (data && organization) {
          const payload: OrganizationUpdateMutationVariables = {
            id: organization._id,
            record: {
              name: updateMultilingual(organization.name, data.name ?? ""),
              description: updateMultilingual(
                organization.description,
                data.description ?? "",
              ),
              shortDescription: updateMultilingual(
                organization.shortDescription,
                data.shortDescription ?? "",
              ),
              contact: {
                email:
                  data.email && data.email.length > 0 ? data.email : undefined,
                phone:
                  data.phone && data.phone.length > 0 ? data.phone : undefined,
                address: {
                  line1: data.fullAddressObj?.line1 ?? "",
                  line2: data.fullAddressObj?.line2 ?? "",
                  city: data.fullAddressObj?.city ?? "",
                  subdivision: data.fullAddressObj?.subdivision ?? "",
                  postalCode: data.fullAddressObj?.postalCode ?? "",
                  country: data.fullAddressObj?.country ?? "",
                  location:
                    data.fullAddressObj?.location &&
                    data.fullAddressObj?.location?.coordinates?.lat &&
                    data.fullAddressObj?.location?.coordinates?.lng
                      ? {
                          type:
                            (data.fullAddressObj.location
                              ?.type as EnumAddressLocationType) ??
                            EnumAddressLocationType.Point,
                          coordinates: [
                            data.fullAddressObj.location.coordinates.lng,
                            data.fullAddressObj.location?.coordinates.lat,
                          ],
                        }
                      : undefined,
                },
                links: {
                  website: data.website ?? "",
                  facebook:
                    data.facebook && data.facebook.length > 0
                      ? data.facebook
                      : undefined,
                  instagram:
                    data.instagram && data.instagram.length > 0
                      ? data.instagram
                      : undefined,
                  youtube:
                    data.youtube && data.youtube.length > 0
                      ? data.youtube
                      : undefined,
                  pinterest:
                    data.pinterest && data.pinterest.length > 0
                      ? data.pinterest
                      : undefined,
                  googleMyBusiness:
                    data.googleMyBusiness && data.googleMyBusiness.length > 0
                      ? data.googleMyBusiness
                      : undefined,
                  tiktok:
                    data.tiktok && data.tiktok.length > 0
                      ? data.tiktok
                      : undefined,
                },
              },
            },
          };

          await updateOrganization({
            variables: payload,
          });

          toastFactory.create({
            title: t("core:SUCCESS"),
            description: t(
              "directory:ORGANIZATION.PROFILE.FORM.UPDATE.SUCCESS",
              {
                name: data.name,
              },
            ),
          });
        } else {
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: t("directory:ORGANIZATION.PROFILE.FORM.UPDATE.ERROR", {
              name: data?.name ?? t("directory:ORGANIZATION.YOUR"),
            }),
          });
        }
      },
      [organization, t, updateOrganization],
    ),
    defaultValues,
    { schema: organizationProfileSchema },
  );
};
